import { useEffectEvent, useLayoutEffect, useMemo, useRef } from 'react';
import seedrandom from 'seedrandom';
import * as THREE from 'three';
import type { HexTileMap, HexTileMapKey, HexTileType } from '@/game/hexmap';
import { axialToWorld, WHEAT_COLOR } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import { exploredColor } from '@/game/hexmap/view/tiles/sharedMaterials';
import type { RuleSet } from '@/game/rules';
import {
  buildingIndex,
  fromMilli,
  type PlayerId,
  type SimState,
  type TileVisibility,
} from '@/game/sim';
import type { TileView } from '@/game/view/lastSeen';
import { citySprawl, type SprawlTile, visibleSprawl } from '@/game/view/sprawl';
import { useSimFrame } from './app/simFrame';

/**
 * Props on resource tiles: cone trees on forests, wheat tufts on fields.
 *
 * - How many props a tile gets scales with its starting resourceValue, and
 *   they disappear one by one (in a per-tile random order) as it is gathered,
 *   so a glance shows how rich a tile is and how much is left.
 * - Every tile has its own layout, seeded from its coordinates, so the same
 *   map always looks the same on every peer.
 * - Hidden on unexplored tiles, under buildings and under city sprawl (own
 *   cities' always, rivals' only with their city in sight: `visibleSprawl`,
 *   as `CitySprawlLayer` draws it),
 *   dimmed on explored tiles.
 *
 * One InstancedMesh per prop kind. Layouts are built once per map; after that
 * only tiles whose shown count or visibility changed have their instances
 * rewritten (a full pass only when fog-of-war changes).
 */

interface Decoration {
  tileType: HexTileType;
  geometry: THREE.BufferGeometry;
  color: THREE.Color;
  /** Props on a tile with this much resource: clamp(round(value / perUnit), min, max). */
  perUnit: number;
  min: number;
  max: number;
  /** Minimum centre-to-centre spacing between props, in hex radii. */
  spacing: number;
  scaleRange: readonly [number, number];
}

function cone(radius: number, height: number): THREE.ConeGeometry {
  const geometry = new THREE.ConeGeometry(radius, height, 6);
  geometry.translate(0, height / 2, 0);
  return geometry;
}

const DECORATIONS: readonly Decoration[] = [
  {
    tileType: 'forest',
    geometry: cone(0.18, 0.5),
    color: new THREE.Color('#1c4a21'),
    perUnit: 2.5,
    min: 2,
    max: 7,
    spacing: 0.3,
    scaleRange: [0.75, 1.2],
  },
  {
    tileType: 'field',
    geometry: cone(0.05, 0.26),
    color: new THREE.Color(WHEAT_COLOR),
    perUnit: 0.75,
    min: 8,
    max: 26,
    spacing: 0.115,
    scaleRange: [0.8, 1.15],
  },
];

/** Props stay inside this radius (in hex radii) so they don't spill over edges. */
const PROP_AREA_RADIUS = 0.62;
const material = new THREE.MeshStandardMaterial({ color: 'white' });
const noRaycast = () => null;
const NO_SPRAWL: ReadonlyMap<HexTileMapKey, SprawlTile> = new Map();

interface TileLayout {
  key: HexTileMapKey;
  /** First instance index for this tile; it owns `props.length` slots. */
  start: number;
  initial: number;
  /** Local offsets in removal order: the last entry is gathered first. */
  props: Array<{ x: number; z: number; scale: number; yaw: number }>;
}

function buildLayouts(
  tiles: HexTileMap,
  d: Decoration,
  density: number
): { layouts: TileLayout[]; total: number } {
  const layouts: TileLayout[] = [];
  let total = 0;
  for (const [key, tile] of tiles) {
    if (tile.type !== d.tileType) continue;
    const full = Math.max(d.min, Math.min(d.max, Math.round(tile.resourceValue / d.perUnit)));
    // Thinned (Low quality): the first props of the same layout.
    const count = Math.max(1, Math.round(full * density));
    const rng = seedrandom(`decor:${key}`);
    const props: TileLayout['props'] = [];
    // Rejection sampling for an evenly spread but irregular layout.
    for (let attempt = 0; props.length < count && attempt < count * 40; attempt++) {
      const angle = rng() * Math.PI * 2;
      const dist = Math.sqrt(rng()) * PROP_AREA_RADIUS;
      const x = Math.cos(angle) * dist;
      const z = Math.sin(angle) * dist;
      if (props.some((p) => Math.hypot(p.x - x, p.z - z) < d.spacing)) continue;
      const [lo, hi] = d.scaleRange;
      props.push({ x, z, scale: lo + rng() * (hi - lo), yaw: rng() * Math.PI * 2 });
    }
    layouts.push({ key, start: total, initial: tile.resourceValue, props });
    total += props.length;
  }
  return { layouts, total };
}

interface TileDecorationsProps {
  tiles: HexTileMap;
  /** Converts a tile's resourceValue into the gatherable amount the sim tracks. */
  tileYield: number;
  /** The state shown. */
  state: SimState;
  /** By sight; a new function whenever the fog changes, which redraws every tile. */
  getTileVisibility: (key: HexTileMapKey) => TileVisibility;
  /**
   * What the player knows about tiles (remembered state out of sight); a new
   * lookup each tick, which redraws the tiles being gathered or built on.
   */
  tileView: (key: HexTileMapKey) => TileView;
  /** For city sprawl; without it (the title screen) no tile is sprawl. */
  rules?: RuleSet;
  playerId?: PlayerId;
  /** Fraction of each tile's props drawn (default 1; Low quality thins them). */
  density?: number;
}

function DecorationLayer({
  decoration,
  tiles,
  tileYield,
  state,
  getTileVisibility,
  tileView,
  rules,
  playerId,
  density = 1,
}: TileDecorationsProps & { decoration: Decoration }) {
  const { layouts, total } = useMemo(
    () => buildLayouts(tiles, decoration, density),
    [tiles, decoration, density]
  );
  const byKey = useMemo(() => new Map(layouts.map((l) => [l.key, l])), [layouts]);
  const meshRef = useRef<THREE.InstancedMesh>(null);
  // Per tile: "shown:visibility" last written, so unchanged tiles are skipped.
  const writtenRef = useRef(new Map<HexTileMapKey, string>());
  // Tiles currently hidden because a building or city sprawl stands on them.
  const coveredRef = useRef(new Set<HexTileMapKey>());
  // New layouts come with a new mesh: everything is written afresh.
  const layoutsRef = useRef(layouts);

  function writeTile(
    mesh: THREE.InstancedMesh,
    layout: TileLayout,
    view: (key: HexTileMapKey) => TileView,
    sprawlOwner: (key: HexTileMapKey) => PlayerId | undefined,
    sight: (key: HexTileMapKey) => TileVisibility
  ): boolean {
    const visibility = sight(layout.key);
    // Out of sight this is the tile as last seen, not live rival activity.
    const known = view(layout.key);
    // Remaining is in thousandths of gatherable units (resourceValue × tileYield).
    const remaining =
      known.remaining === null ? layout.initial : fromMilli(known.remaining) / tileYield;
    const sprawl = sprawlOwner(layout.key);
    // Only sprawl the viewer is shown (`visibleSprawl`) hides props.
    const covered = known.building !== null || sprawl !== undefined;
    if (covered) coveredRef.current.add(layout.key);
    else coveredRef.current.delete(layout.key);
    const shown =
      visibility === 'unexplored' || covered || remaining <= 0
        ? 0
        : Math.ceil((layout.props.length * remaining) / Math.max(1, layout.initial));
    const signature = `${shown}:${visibility}`;
    if (writtenRef.current.get(layout.key) === signature) return false;
    writtenRef.current.set(layout.key, signature);

    const [q, r] = layout.key.split(',').map(Number);
    const world = axialToWorld({ q, r }, config.hexMap.tile.radius);
    const color = visibility === 'visible' ? decoration.color : exploredColor(decoration.color);
    const dummy = new THREE.Object3D();
    layout.props.forEach((p, i) => {
      dummy.position.set(
        world.x + p.x * config.hexMap.tile.radius,
        config.hexMap.tile.thickness,
        world.z + p.z * config.hexMap.tile.radius
      );
      dummy.rotation.set(0, p.yaw, 0);
      dummy.scale.setScalar(i < shown ? p.scale : 0);
      dummy.updateMatrix();
      mesh.setMatrixAt(layout.start + i, dummy.matrix);
      mesh.setColorAt(layout.start + i, color);
    });
    return true;
  }

  function sprawlOf(state: SimState): ReadonlyMap<HexTileMapKey, SprawlTile> {
    if (!rules || playerId === undefined) return NO_SPRAWL;
    return visibleSprawl(
      citySprawl(state, { terrain: tiles, rules }),
      state,
      playerId,
      getTileVisibility
    );
  }

  function flush(mesh: THREE.InstancedMesh) {
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }

  // Full pass: new map or fog-of-war change.
  const fullPass = useEffectEvent(
    (layouts: TileLayout[], sight: (key: HexTileMapKey) => TileVisibility) => {
      const mesh = meshRef.current;
      if (!mesh) return;
      if (layoutsRef.current !== layouts) {
        layoutsRef.current = layouts;
        writtenRef.current.clear();
      }
      const sprawlMap = sprawlOf(state);
      const sprawl = (key: HexTileMapKey) => sprawlMap.get(key)?.owner;
      let changed = false;
      for (const layout of layouts) {
        changed = writeTile(mesh, layout, tileView, sprawl, sight) || changed;
      }
      if (changed) flush(mesh);
    }
  );
  useLayoutEffect(() => fullPass(layouts, getTileVisibility), [layouts, getTileVisibility]);

  // Incremental pass each tick: only tiles being gathered or built on can change.
  const tickPass = useEffectEvent((view: (key: HexTileMapKey) => TileView) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    // Every tile a building covers (a grown farm's extra tiles too).
    const buildingKeys = new Set(buildingIndex(state).keys());
    const sprawlMap = sprawlOf(state);
    // Gathered tiles, built-on or sprawl tiles, and previously covered tiles
    // (whose building or sprawl may be gone) are the only ones that can change.
    const keys = new Set<string>([
      ...Object.keys(state.tileResourceOverride),
      ...buildingKeys,
      ...sprawlMap.keys(),
      ...coveredRef.current,
    ]);
    const sprawl = (key: HexTileMapKey) => sprawlMap.get(key)?.owner;
    let changed = false;
    for (const key of keys) {
      const layout = byKey.get(key as HexTileMapKey);
      if (layout) changed = writeTile(mesh, layout, view, sprawl, getTileVisibility) || changed;
    }
    if (changed) flush(mesh);
  });
  useLayoutEffect(() => tickPass(tileView), [tileView]);

  return (
    <instancedMesh
      ref={meshRef}
      args={[decoration.geometry, material, Math.max(1, total)]}
      frustumCulled={false}
      raycast={noRaycast}
    />
  );
}

export function TileDecorations(props: TileDecorationsProps) {
  return (
    <>
      {DECORATIONS.map((decoration) => (
        <DecorationLayer key={decoration.tileType} decoration={decoration} {...props} />
      ))}
    </>
  );
}

/** The match's tile props, from the current frame: rivals' tiles as last seen. */
export function MatchTileDecorations({ density }: { density: number }) {
  const frame = useSimFrame();
  return (
    <TileDecorations
      tiles={frame.terrain}
      tileYield={frame.rules.pacing.tileYield}
      density={density}
      state={frame.view}
      getTileVisibility={frame.fog.sight}
      tileView={frame.tileView}
      rules={frame.rules}
      playerId={frame.playerId}
    />
  );
}
